import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableScroll, Badge, SortableTh, EmptyValue, ClampText, IdLink, RowActions, ACTIONS_COL_WIDTH } from '@/components/ui';
import { humanizeCode } from '../../../utils/statusUtils';
import { Vehicle } from '../../../types/Vehicle';
import VehicleProfilePreview from './VehicleProfilePreview';

interface VehiclesTableProps {
  vehicles: Vehicle[];
  onSelectVehicle?: (vehicle: Vehicle) => void;
  sortBy?: string | null;
  loading?: boolean;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

const VehiclesTable: React.FC<VehiclesTableProps> = ({ vehicles, onSelectVehicle, sortBy = null, sortDir = 'asc', onSort, loading = false }) => {
  const navigate = useNavigate();
  /** V3-025: the ⋮ opens the vehicle on one of its record sections. */
  const openSection = (vehicle: Vehicle, section: string) => navigate(`/vehicles/${vehicle.id}?section=${section}`);

  const handleRowClick = (vehicle: Vehicle) => {
    if (onSelectVehicle) {
      onSelectVehicle(vehicle);
    }
  };

  // TableScroll is the page's one scroll box: sticky header, Unit Number pinned left and Actions
  // pinned right while the other columns scroll sideways (W-H5, W-M8).
  return (
    <TableScroll>
      <Table hover stickyHeader>
        <thead>
          <tr>
            <SortableTh label="Unit Number" sortKey="unitNumber" activeKey={sortBy} direction={sortDir} onSort={onSort} className="wrap-pin-left wrap-pin-edge" />
            <SortableTh label="Description" sortKey="description" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ minWidth: '18rem' }} />
            <SortableTh label="Vehicle Type" sortKey="vehicleType" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Vehicle Classification" sortKey="vehicleClassification" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Current Status" sortKey="currentStatus" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <th>Service Areas</th>
            <th>Current Yard</th>
            <th>Profile</th>
            <th className="wrap-pin-right tw-text-right" style={{ width: ACTIONS_COL_WIDTH }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.length === 0 ? (
            <tr>
              <td colSpan={9} className="tw-text-center tw-py-4">
                {loading ? 'Loading vehicles...' : 'No vehicles found'}
              </td>
            </tr>
          ) : (
            vehicles.map((vehicle) => (
              <tr key={vehicle.id} onClick={() => handleRowClick(vehicle)} style={{ cursor: 'pointer' }}>
                {/* V2-072 / ACT-01: the unit number is the link that opens the vehicle (no View button). */}
                <td className="wrap-pin-left wrap-pin-edge">
                  <IdLink
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(vehicle);
                    }}
                  >
                    {vehicle.unitNumber}
                  </IdLink>
                </td>
                <td>{vehicle.description ? <ClampText>{vehicle.description}</ClampText> : <EmptyValue />}</td>
                <td>{vehicle.vehicleType || <EmptyValue />}</td>
                <td>{vehicle.vehicleClassification || <EmptyValue />}</td>
                <td>
                  {/* Friendly label, never the stored code: "ACTIVE" reads "Active" (W-M5). */}
                  <Badge pill bg={vehicle.currentStatus === 'In Service' ? 'success' : 'secondary'}>
                    {humanizeCode(vehicle.currentStatus || vehicle.status || '')}
                  </Badge>
                </td>
                <td>
                  {vehicle.vehicleServiceAreas && vehicle.vehicleServiceAreas.length > 0
                    ? vehicle.vehicleServiceAreas.map(sa => sa.serviceArea.name).join(', ')
                    : <EmptyValue />}
                </td>
                <td>{vehicle.currentYard?.name || <EmptyValue />}</td>
                <td>
                  {vehicle.vehicleProfile ? (
                    <div className="tw-flex tw-items-center">
                      <span>{vehicle.vehicleProfile.inspection}</span>
                      <VehicleProfilePreview profile={vehicle.vehicleProfile} />
                    </div>
                  ) : (
                    <EmptyValue />
                  )}
                </td>
                {/* V3-025 / ACT-02: one ⋮ at the far right for the vehicle's other views. */}
                <td className="wrap-pin-right tw-text-right">
                  <RowActions
                    actions={[
                      { kind: 'custom', icon: 'bi-exclamation-triangle', label: 'Vehicle Defects', onClick: () => openSection(vehicle, 'vehicle-defects') },
                      { kind: 'custom', icon: 'bi-tools', label: 'Preventative Maintenance', onClick: () => openSection(vehicle, 'preventative-maintenance') },
                      { kind: 'custom', icon: 'bi-wrench', label: 'Repair History', onClick: () => openSection(vehicle, 'repair-history') },
                      { kind: 'history', label: 'Vehicle History', onClick: () => openSection(vehicle, 'vehicle-history') },
                    ]}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default VehiclesTable;

